import { Box, Grid, MenuItem } from "@mui/material";
import { useEffect, useState } from "react";
import Input from "../../../../../components/Input";
import { useVehicleStore } from "../../../../../store/useVehicleStore";
import { getFleetBranches, updateVehicleDetails } from "../../../../../services/dashboard";
import { useToast } from "../../../../../hooks/useToast";
import { toast } from "react-toastify";
import { useAllManufacturer } from "../../../../../hooks/useAllManufacturer";
import { useAllModels } from "../../../../../hooks/useAllModals";
import {
  OperationsDetailField,
  OperationsDetailGrid,
} from "../../../ifas/components/OperationsUI";

export default function Details({
  isEditing,
  setIsEditing,
  registerUpdateHandler,
}: {
  isEditing: boolean;
  setIsEditing: (value: boolean) => void;
  registerUpdateHandler?: (fn: (() => Promise<void>) | null) => void;
}) {
  const { selectedVehicle, setSelectedVehicle } = useVehicleStore();
  const { showInfoToast } = useToast();
  const { manufacturer } = useAllManufacturer();
  const { models, fetchModels } = useAllModels();
  const vehicle = selectedVehicle;
  const [isUpdating, setIsUpdating] = useState(false);
  const [fleetBranches, setFleetBranches] = useState<any[]>([]);
  const [formData, setFormData] = useState({
    vehicleType: "",
    vehicleRegistrationNumber: "",
    manufacturerId: "",
    modelId: "",
    vehicleColor: "",
    materialSource: "",
    branchId: "",
    branchLocation: "",
    fleetCategory: "",
    dayDelivery: "",
    currentMileage: "",
    requiredLicenseCode: "",
    requiresPdp: false,
  });

  useEffect(() => {
    const loadBranches = async () => {
      const response = await getFleetBranches();
      if (response?.status === 200 && Array.isArray(response.data)) {
        setFleetBranches(
          [...response.data].sort((a: any, b: any) =>
            String(a.branchName || "").localeCompare(String(b.branchName || "")),
          ),
        );
      }
    };

    void loadBranches();
  }, []);

  useEffect(() => {
    if (!vehicle) return;
    setFormData({
      vehicleType: vehicle.vehicleType || "",
      vehicleRegistrationNumber: vehicle.vehicleRegistrationNumber || "",
      manufacturerId: vehicle.manufacturerId ? String(vehicle.manufacturerId) : "",
      modelId: vehicle.modelId ? String(vehicle.modelId) : "",
      vehicleColor: vehicle.vehicleColor || "",
      materialSource: vehicle.materialSource || "",
      branchId: vehicle.branchId || "",
      branchLocation: vehicle.branchName || vehicle.branchLocation || "",
      fleetCategory: vehicle.fleetCategory || "",
      dayDelivery: vehicle.dayDelivery || "",
      currentMileage: vehicle.currentMileage == null ? "" : String(vehicle.currentMileage),
      requiredLicenseCode: vehicle.requiredLicenseCode || "",
      requiresPdp: Boolean(vehicle.requiresPdp),
    });
    if (vehicle.manufacturerId) {
      fetchModels(String(vehicle.manufacturerId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicle]);

  const handleFieldChange =
    (field: keyof typeof formData) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;

      if (field === "manufacturerId") {
        fetchModels(value);
        setFormData((prev) => ({ ...prev, manufacturerId: value, modelId: "" }));
        return;
      }

      if (field === "branchId") {
        const selectedBranch = fleetBranches.find(
          (branch: any) => String(branch.id) === String(value),
        );
        setFormData((prev) => ({
          ...prev,
          branchId: value,
          branchLocation: selectedBranch?.branchName || "",
        }));
        return;
      }

      setFormData((prev) => ({ ...prev, [field]: value }));
    };

  const handleUpdate = async () => {
    if (!vehicle?.vehicleID) {
      showInfoToast("Unable to identify the selected vehicle");
      return;
    }

    if (!formData.vehicleType) {
      showInfoToast("Please select a vehicle type");
      return;
    }

    setIsUpdating(true);
    try {
      const payload: any = {
        vehicleType: formData.vehicleType,
        vehicleRegistrationNumber: formData.vehicleRegistrationNumber,
        branchId: formData.branchId || undefined,
        branchLocation: formData.branchLocation || undefined,
        manufacturerId:
          formData.manufacturerId === "" ? undefined : Number(formData.manufacturerId),
        modelId: formData.modelId === "" ? undefined : Number(formData.modelId),
        vehicleColor: formData.vehicleColor || undefined,
        fleetCategory: formData.fleetCategory || undefined,
        dayDelivery: formData.dayDelivery || undefined,
        materialSource: formData.materialSource || undefined,
        currentMileage:
          formData.currentMileage === "" ? undefined : Number(formData.currentMileage),
        requiredLicenseCode: formData.requiredLicenseCode || undefined,
        requiresPdp: Boolean(formData.requiresPdp),
      };
      const res = await updateVehicleDetails(vehicle.vehicleID, payload);
      if (res?.status === 200 || res?.status === 201) {
        const selectedBranch = fleetBranches.find(
          (branch: any) => String(branch.id) === String(formData.branchId),
        );
        const selectedManufacturer = manufacturer.find(
          (item: any) => String(item.manufacturerId) === String(formData.manufacturerId),
        );
        const selectedModel = models.find(
          (item: any) => String(item.modelId) === String(formData.modelId),
        );
        setSelectedVehicle({
          ...vehicle,
          ...payload,
          branchName: selectedBranch?.branchName || vehicle.branchName,
          branchLocation: selectedBranch?.branchName || formData.branchLocation,
          manufacturerName:
            selectedManufacturer?.vehicleManufacturerName || vehicle.manufacturerName,
          modelName: selectedModel?.modelName || vehicle.modelName,
        });
        setIsEditing(false);
        toast.success("Vehicle details updated successfully");
      } else {
        showInfoToast(res?.data?.message || "Failed to update vehicle");
      }
    } catch (e) {
      showInfoToast("Failed to update vehicle");
    } finally {
      setIsUpdating(false);
    }
  };

  useEffect(() => {
    registerUpdateHandler?.(isEditing ? handleUpdate : null);
    return () => registerUpdateHandler?.(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing, vehicle?.vehicleID, formData, fleetBranches, manufacturer, models]);

  const readOnlyBackground = isEditing ? "#ffffff" : "#f5f5f5";

  if (!isEditing) {
    return (
      <OperationsDetailGrid>
        <OperationsDetailField label="Vehicle Number" value={vehicle?.vehicleNumber} />
        <OperationsDetailField label="Vehicle Type" value={vehicle?.vehicleType} />
        <OperationsDetailField
          label="Registration Number"
          value={vehicle?.vehicleRegistrationNumber}
        />
        <OperationsDetailField label="Vehicle Model" value={vehicle?.modelName} />
        <OperationsDetailField label="Manufacturer" value={vehicle?.manufacturerName} />
        <OperationsDetailField label="Color" value={vehicle?.vehicleColor} />
        <OperationsDetailField label="Branch" value={vehicle?.branchName || vehicle?.branchLocation} />
        <OperationsDetailField
          label="Current Mileage"
          value={vehicle?.currentMileage != null ? `${vehicle.currentMileage} km` : ""}
        />
        <OperationsDetailField label="Material Source" value={vehicle?.materialSource} />
        <OperationsDetailField label="Fleet Category" value={vehicle?.fleetCategory} />
        <OperationsDetailField label="Day Delivery" value={vehicle?.dayDelivery} />
        <OperationsDetailField label="Required Licence Code" value={vehicle?.requiredLicenseCode} />
        <OperationsDetailField label="PDP Required" value={vehicle?.requiresPdp ? "Yes" : "No"} />
        <OperationsDetailField label="Assigned Driver" value={vehicle?.assignedDriverName} />
        <OperationsDetailField label="Assigned Driver Number" value={vehicle?.assignedDriverNumber} />
        <OperationsDetailField label="Current Case" value={vehicle?.currentCaseNumber} />
        <OperationsDetailField
          label="Allocation Number"
          value={vehicle?.currentAllocationNumber}
        />
        <OperationsDetailField
          label="Current Body Removal"
          value={vehicle?.currentBodyRemovalNumber}
        />
        <OperationsDetailField
          label="Dispatch Status"
          value={vehicle?.currentBodyRemovalStatus}
        />
        <OperationsDetailField label="Status" value={vehicle?.status} />
      </OperationsDetailGrid>
    );
  }

  return (
    <Grid
      container
      spacing={2}
      mt={1}
      textAlign="left"
      p={3}
    >
      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          select
          disabled={!isEditing}
          value={formData.vehicleType}
          onChange={handleFieldChange("vehicleType")}
          height={40}
          width="100%"
          label="Vehicle Type"
          backgroundColor={readOnlyBackground}
        >
          <MenuItem value="HEARSE">Hearse</MenuItem>
          <MenuItem value="FAMILY_CAR">Family Car</MenuItem>
          <MenuItem value="UTILITY_VEHICLE">Utility Vehicle</MenuItem>
          <MenuItem value="MOTORBIKE">Motorbike</MenuItem>
        </Input>
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          disabled={!isEditing}
          value={formData.vehicleRegistrationNumber}
          onChange={handleFieldChange("vehicleRegistrationNumber")}
          height={40}
          width="100%"
          label="Registration Number"
          backgroundColor={readOnlyBackground}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          select
          disabled={!isEditing || !formData.manufacturerId}
          value={formData.modelId}
          onChange={handleFieldChange("modelId")}
          height={40}
          width="100%"
          label="Vehicle Model"
          backgroundColor={readOnlyBackground}
        >
          <MenuItem value="">Select model</MenuItem>
          {models.map((model: any) => (
            <MenuItem key={model.modelId} value={model.modelId}>
              {model.modelName}
            </MenuItem>
          ))}
        </Input>
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          select
          disabled={!isEditing}
          value={formData.manufacturerId}
          onChange={handleFieldChange("manufacturerId")}
          height={40}
          width="100%"
          label="Manufacturer"
          backgroundColor={readOnlyBackground}
        >
          <MenuItem value="">Select manufacturer</MenuItem>
          {manufacturer.map((item: any) => (
            <MenuItem key={item.manufacturerId} value={item.manufacturerId}>
              {item.vehicleManufacturerName}
            </MenuItem>
          ))}
        </Input>
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          disabled={!isEditing}
          value={formData.vehicleColor}
          onChange={handleFieldChange("vehicleColor")}
          height={40}
          width="100%"
          label="Color"
          backgroundColor={readOnlyBackground}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          select
          disabled={!isEditing}
          value={formData.branchId}
          onChange={handleFieldChange("branchId")}
          height={40}
          width="100%"
          label="Branch"
          backgroundColor={readOnlyBackground}
        >
          <MenuItem value="">Select branch</MenuItem>
          {fleetBranches.map((branch: any) => (
            <MenuItem key={branch.id} value={branch.id}>
              {branch.branchName}
              {branch.province ? ` - ${branch.province}` : ""}
            </MenuItem>
          ))}
        </Input>
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          select
          disabled={!isEditing}
          value={formData.materialSource}
          onChange={handleFieldChange("materialSource")}
          height={40}
          width="100%"
          label="Material Source"
          backgroundColor={readOnlyBackground}
        >
          <MenuItem value="">Select source</MenuItem>
          <MenuItem value="BRANCH_STOCK">Branch Stock</MenuItem>
          <MenuItem value="WAREHOUSE">Warehouse</MenuItem>
          <MenuItem value="MORTUARY">Mortuary</MenuItem>
          <MenuItem value="SUPPLIER">Supplier</MenuItem>
        </Input>
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          select
          disabled={!isEditing}
          value={formData.fleetCategory}
          onChange={handleFieldChange("fleetCategory")}
          height={40}
          width="100%"
          label="Fleet Category"
          backgroundColor={readOnlyBackground}
        >
          <MenuItem value="">Select category</MenuItem>
          <MenuItem value="HOME_TO_HOME">Home-to-Home</MenuItem>
          <MenuItem value="HOME_TO_CEMETERY">Home to Cemetery</MenuItem>
          <MenuItem value="HALL_GROUND_TO_CEMETERY">Hall/Ground to Cemetery</MenuItem>
          <MenuItem value="CHURCH_TO_HOME">Church to Home</MenuItem>
        </Input>
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          select
          disabled={!isEditing}
          value={formData.dayDelivery}
          onChange={handleFieldChange("dayDelivery")}
          height={40}
          width="100%"
          label="Day Delivery"
          backgroundColor={readOnlyBackground}
        >
          <MenuItem value="">Select delivery day</MenuItem>
          <MenuItem value="MORNING_DELIVERY">Morning Delivery</MenuItem>
          <MenuItem value="MONDAY_DELIVERY">Monday Delivery</MenuItem>
          <MenuItem value="TUESDAY_DELIVERY">Tuesday Delivery</MenuItem>
          <MenuItem value="WEDNESDAY_DELIVERY">Wednesday Delivery</MenuItem>
          <MenuItem value="THURSDAY_DELIVERY">Thursday Delivery</MenuItem>
          <MenuItem value="FRIDAY_DELIVERY">Friday Delivery</MenuItem>
        </Input>
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          disabled={!isEditing}
          value={formData.currentMileage}
          onChange={handleFieldChange("currentMileage")}
          type="number"
          height={40}
          width="100%"
          label="Current Mileage"
          backgroundColor={readOnlyBackground}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          select
          disabled={!isEditing}
          value={formData.requiredLicenseCode}
          onChange={handleFieldChange("requiredLicenseCode")}
          height={40}
          width="100%"
          label="Required Licence Code"
          backgroundColor={readOnlyBackground}
        >
          <MenuItem value="">Not specified</MenuItem>
          <MenuItem value="CODE_8">Code 8</MenuItem>
          <MenuItem value="CODE_10">Code 10</MenuItem>
          <MenuItem value="CODE_14">Code 14</MenuItem>
        </Input>
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          select
          disabled={!isEditing}
          value={String(formData.requiresPdp)}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            setFormData((prev) => ({
              ...prev,
              requiresPdp: event.target.value === "true",
            }))
          }
          height={40}
          width="100%"
          label="PDP Required"
          backgroundColor={readOnlyBackground}
        >
          <MenuItem value="false">No</MenuItem>
          <MenuItem value="true">Yes</MenuItem>
        </Input>
      </Grid>

      {/* Update action is rendered in the page header action bar for consistency */}


      {/* {comments?.length > 0 && (
        <Grid size={12} mt={3}>
          {comments.map((comment: any, index: number) => (
            <Accordion
              key={index}
              sx={{
                backgroundColor: "#F7F7F7",
                boxShadow: "none",
                borderRadius: "6px",
                mb: 2,
                border: "1px solid #E0E0E0",
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls={`panel${index}-content`}
                id={`panel${index}-header`}
                sx={{
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Box>
                  <Typography fontWeight={600} fontSize={14}>
                    {comment.commentedByName}{" "}
                    <Typography
                      component="span"
                      fontWeight={600}
                      color="#EC6E00"
                    >
                      - {comment.role_at_time_of_comment?.replace(/_/g, " ").toUpperCase()}
                    </Typography>
                  </Typography>
                  <Typography fontSize={12} color="text.secondary">
                    {new Date(comment.created_on).toLocaleTimeString("en-US", {
                      hour: "numeric",
                      minute: "2-digit",
                    })}{" "}
                    | {formatDate(comment.created_on)}
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Typography fontSize={15} color="#333" lineHeight={1.6}>
                  {comment.content}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Grid>
      )}
      {isAssigned && claimStatus !== "PAID" && (
        <Grid size={{ md: 12 }} mt={3}>
          <Typography
            variant="body1"
            fontWeight={600}
            fontSize={18}
            sx={{ display: "block", textAlign: "left" }}
          >
            Comment
          </Typography>
          <TextField
            multiline
            rows={4}
            variant="outlined"
            fullWidth
            disabled={isEditable}
            value={policyComment}
            onChange={onPolicyCommentChange}
          />
        </Grid>
      )}
      <Dialog
        open={openPreview}
        onClose={handleClosePreview}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Document Preview</DialogTitle>
        <DialogContent dividers>
          {selectedDoc ? (
            <object
              data={selectedDoc.file_url}
              type="application/pdf"
              width="100%"
              height="600px"
            >
              <p>
                PDF preview is not supported in this browser.{" "}
                <a
                  href={selectedDoc.file_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Download PDF
                </a>
              </p>
            </object>
          ) : (
            <Typography>Loading...</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => handleDownload(selectedDoc)}
            color="primary"
            variant="outlined"
          >
            Download
          </Button>

          <Button
            onClick={handleClosePreview}
            color="secondary"
            variant="contained"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog> */}
    </Grid>
  );
}
